<script lang="ts">
  // Single component for both 'add' and 'edit' NVR. Mode is set via prop.
  // 'edit' locks the id field and allows submit even with empty creds —
  // server returns 400 if creds wrong. (Future: add a 'preserve old creds'
  // flag and a separate /api/nvrs/{id}/creds endpoint.)
  import type { NvrInfo } from '../lib/types';
  import { createNvr, putNvr } from '../lib/api';

  type Props = {
    mode: 'add' | 'edit';
    nvr?: NvrInfo;
    onsuccess: (id: string) => void;
    onclose: () => void;
  };
  let { mode, nvr, onsuccess, onclose }: Props = $props();

  let name = $state(nvr?.name ?? '');
  let id = $state(nvr?.id ?? '');
  let host = $state('');
  let port = $state(554);
  let user = $state('admin');
  let password = $state('');
  let channels = $state(nvr?.stream_count || 16);
  let urlTpl = $state('rtsp://{user}:{pass}@{host}:{port}/unicast/c{ch}/s0/live');
  let note = $state(nvr?.note ?? '');
  let err = $state('');
  let busy = $state(false);
  let idTouched = $state(false);

  const RUS_LAT: Record<string, string> = {
    а:'a',б:'b',в:'v',г:'g',д:'d',е:'e',ё:'e',ж:'zh',з:'z',и:'i',й:'y',к:'k',л:'l',м:'m',
    н:'n',о:'o',п:'p',р:'r',с:'s',т:'t',у:'u',ф:'f',х:'h',ц:'c',ч:'ch',ш:'sh',щ:'shch',
    ъ:'',ы:'y',ь:'',э:'e',ю:'yu',я:'ya',
  };
  function slugify(s: string): string {
    return s
      .toLowerCase()
      .split('')
      .map((c) => RUS_LAT[c] ?? c)
      .join('')
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 32);
  }

  $effect(() => {
    if (mode === 'add' && !idTouched) id = slugify(name);
  });

  async function submit(e: Event): Promise<void> {
    e.preventDefault();
    err = '';
    busy = true;
    try {
      if (mode === 'add') {
        await createNvr({
          id, name, note, host, port, user, password, channels, url_template: urlTpl,
        });
      } else {
        await putNvr(nvr!.id, {
          name, note, host, port, user, password, channels, url_template: urlTpl,
        });
      }
      onsuccess(mode === 'add' ? id : nvr!.id);
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      busy = false;
    }
  }

  function onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') onclose();
  }
</script>

<svelte:window onkeydown={onKeydown} />

<div class="bg" onclick={(e) => { if (e.target === e.currentTarget) onclose(); }} role="presentation">
  <form class="modal" onsubmit={submit}>
    <h2>{mode === 'add' ? 'Добавить рег' : 'Настройки рега'}</h2>
    <p class="muted">
      {#if mode === 'edit'}
        Введите host/login/pass заново — мы их не возвращаем в API ответе.
      {:else}
        Создаст запись в config.toml и развернёт RTSP-задачи под N каналов на лету.
      {/if}
    </p>
    <label>Имя <input bind:value={name} required maxlength="64" placeholder="Производство, Ростов" /></label>
    <label>ID <input bind:value={id} required pattern="[a-z0-9_\-]+" disabled={mode === 'edit'}
                    oninput={() => { idTouched = true; }} maxlength="32" placeholder="rostov" /></label>
    <div class="grid2">
      <label>Host <input bind:value={host} required maxlength="200" placeholder="80.254.115.94" /></label>
      <label>Порт <input type="number" bind:value={port} min="1" max="65535" /></label>
    </div>
    <div class="grid2">
      <label>Логин <input bind:value={user} required maxlength="64" /></label>
      <label>Пароль <input type="password" bind:value={password} required maxlength="128" /></label>
    </div>
    <div class="grid2">
      <label>Каналов <input type="number" bind:value={channels} min="1" max="256" required /></label>
      <label>Шаблон URL <input bind:value={urlTpl} maxlength="256" /></label>
    </div>
    <label>Заметка <input bind:value={note} maxlength="200" placeholder="UNV NVR302-16S" /></label>
    <div class="err">{err}</div>
    <div class="actions">
      <button type="button" class="ghost" onclick={onclose}>Отмена</button>
      <button type="submit" class="primary" disabled={busy}>
        {busy ? 'Сохраняю…' : mode === 'add' ? 'Добавить' : 'Сохранить'}
      </button>
    </div>
  </form>
</div>

<style>
  .bg { position: fixed; inset: 0; z-index: 100; background: rgba(0,0,0,.45);
        backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
        display: flex; align-items: center; justify-content: center; }
  .modal { width: min(460px, calc(100vw - 32px)); background: var(--bg);
           border: 1px solid var(--border); border-radius: var(--radius);
           padding: 24px; display: flex; flex-direction: column; gap: 12px; }
  h2 { font-size: 17px; font-weight: 600; letter-spacing: -.01em; margin: 0; }
  .muted { color: var(--fg-muted); margin: 0; font-size: 13px; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  label { display: flex; flex-direction: column; gap: 4px; font-size: 12px;
          color: var(--fg-muted); font-weight: 500; }
  input { padding: 8px 10px; border-radius: 8px; border: 1px solid var(--border);
          background: var(--bg-elev); color: var(--fg); font: inherit; }
  input:focus { outline: 2px solid var(--accent); outline-offset: -1px; border-color: var(--accent); }
  input:disabled { opacity: .6; }
  .err { color: var(--bad); font-size: 13px; min-height: 1.2em; }
  .actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 4px; }
  button.primary { background: var(--accent); color: #fff; padding: 8px 14px;
                   border-radius: 8px; font-weight: 500; }
  button.primary:disabled { opacity: .6; cursor: default; }
  button.primary:hover:not(:disabled) { filter: brightness(1.08); }
  button.ghost { color: var(--fg-muted); padding: 8px 14px; border-radius: 8px; font-weight: 500; }
  button.ghost:hover { color: var(--fg); }
</style>
